import { NumberInputWithUnit } from "../../../components/NumberInputWithUnit/NumberInputWithUnit";
import type { SchemaField } from "../../../components/SchemaEditor/SchemaEditor";
import { noIntStatKey } from "../../../lib/schemaFields";
import { StatKeySelect } from "./StatKeySelect";
import { TRIGGER_VALUE_MAX } from "./invites";
import styles from "./InviteTriggerField.module.css";

interface Props {
  statKey: string;
  triggerValue: number;
  statsSchema: SchemaField[];
  onChange: (next: { statKey: string; triggerValue: number }) => void;
  /** The threshold's own error, when it has one — the box may be emptied. */
  error?: string;
}

/**
 * `invites.trigger_stat_key` + `invites.trigger_value` — the pair that decides
 * WHEN a referral pays, drawn as the one control it is.
 *
 * One control rather than two rows because the server reads it as one: a
 * trigger counts as set only while both halves are (`trigger_stat_key !== "" &&
 * trigger_value > 0`), and half of it is not a looser trigger but none at all —
 * both sides are then paid the moment the code is accepted. Two rows would let
 * an operator read the half they filled in and believe the feature is gated.
 *
 * Mockup: .codelane/artifacts/t-162/config-invites.html L362-L383, states at
 * L514-L554.
 */
export function InviteTriggerField({
  statKey,
  triggerValue,
  statsSchema,
  onChange,
  error,
}: Props) {
  const noKeys = noIntStatKey(statsSchema, statKey);
  const armed = statKey !== "" && triggerValue > 0;

  return (
    <>
      <div className={styles.pair}>
        <StatKeySelect
          value={statKey}
          statsSchema={statsSchema}
          onChange={(next) =>
            onChange({
              statKey: next,
              // Clearing the key takes the threshold's box off the screen, so
              // the number goes with it rather than staying in games.config
              // where nothing shows it and nothing can fix it. Half a trigger
              // is no trigger to the server either — this is the same rule,
              // written where an operator can see it happen.
              triggerValue:
                next === "" || !Number.isFinite(triggerValue) ? 0 : triggerValue,
            })
          }
          emptyLabel="— no int fields —"
          className={styles.select}
          aria-label="Reward trigger stat key"
        />
        {statKey !== "" && (
          // Shown for as long as a key is picked, INCLUDING at 0 — that state
          // is a warning below, and a warning about a box that is not there is
          // one nobody can act on.
          <>
            <span className={styles.leadIn}>reaches</span>
            <NumberInputWithUnit
              value={triggerValue}
              onChange={(v) => onChange({ statKey, triggerValue: v })}
              min={0}
              max={TRIGGER_VALUE_MAX}
              error={error}
              aria-label="Reward trigger value"
            />
          </>
        )}
      </div>
      {noKeys ? (
        <div className={`${styles.preview} ${styles.previewWarn}`}>
          The trigger reads an <code>int</code> field of{" "}
          <code>profile.stats_schema</code>, and this project declares none. Add
          one in the <b>Profile</b> section first.
        </div>
      ) : !armed ? (
        <div className={`${styles.preview} ${styles.previewWarn}`}>
          No trigger: both sides are paid the moment the code is accepted, and
          any account can accept one. Pick a stat unless the reward is meant to
          be free.
        </div>
      ) : (
        <div className={styles.preview}>
          Both sides are paid once <code>players.stats.{statKey}</code> reaches{" "}
          <b>{triggerValue}</b>, once per pair and never again. A player already
          at or past it cannot accept a code — the API answers{" "}
          <code>invite_window_closed</code>.
        </div>
      )}
    </>
  );
}
